/**
 * TagPeopleModal — Facebook-style user tag picker.
 * Search bar + Done button at top, tagged pills section, suggestions list.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { BaseModal, MODAL_COLORS } from '@/components/modal';
import { ADMIN_COLORS } from '@/constants/theme';
import { UserAvatar } from '@/components/ui';
import { feedApi } from '../api';
import type { TaggableUser } from '../types';

interface TagPeopleModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTagged: TaggableUser[];
  onDone: (users: TaggableUser[]) => void;
}

function useDebouncedValue(value: string, delay = 300): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function TagPeopleModal({ isOpen, onClose, initialTagged, onDone }: TagPeopleModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<TaggableUser[]>(initialTagged);
  const debouncedSearch = useDebouncedValue(search);

  const { data: suggestionsData } = useQuery({
    queryKey: ['feed', 'users', 'suggestions', debouncedSearch],
    queryFn: async () => {
      const { data } = await feedApi.getUserSuggestions(debouncedSearch || undefined);
      return data.users;
    },
    enabled: isOpen,
  });

  const suggestions = suggestionsData ?? [];
  const selectedIds = useMemo(() => new Set(selected.map((u) => u.id)), [selected]);

  const toggleUser = useCallback((user: TaggableUser) => {
    setSelected((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      return exists ? prev.filter((u) => u.id !== user.id) : [...prev, user];
    });
  }, []);

  const removeUser = useCallback((userId: string) => {
    setSelected((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const handleDone = useCallback(() => {
    onDone(selected);
    onClose();
  }, [selected, onDone, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelected(initialTagged);
    }
  }, [isOpen, initialTagged]);

  const getUserName = (user: TaggableUser) =>
    [user.firstName, user.lastName].filter(Boolean).join(' ') || '?';

  if (!isOpen) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} ariaLabel={t('feed.tagPeople')}>
      <div
        className="flex flex-col h-full max-h-[80vh]"
        style={{ backgroundColor: MODAL_COLORS.surface }}
      >
        {/* Header: Title + Done */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: MODAL_COLORS.border }}
        >
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition-all active:scale-90"
            style={{ color: MODAL_COLORS.text }}
          >
            <X className="h-5 w-5" />
          </button>
          <h2
            className="text-base font-bold"
            style={{ color: MODAL_COLORS.text }}
          >
            {t('feed.tagPeople')}
          </h2>
          <button
            onClick={handleDone}
            className="text-sm font-bold px-3 py-1.5 rounded-lg transition-all active:scale-95"
            style={{ color: ADMIN_COLORS.primary }}
          >
            {t('feed.done')}
          </button>
        </div>

        {/* Search bar */}
        <div className="px-4 py-2">
          <div
            className="flex items-center gap-2 rounded-xl px-3 py-2"
            style={{
              backgroundColor: MODAL_COLORS.interactive,
              border: `1px solid ${MODAL_COLORS.border}`,
            }}
          >
            <Search className="h-4 w-4 flex-shrink-0" style={{ color: '#65676B' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('feed.searchUsers')}
              className="flex-1 text-sm bg-transparent outline-none"
              style={{ color: MODAL_COLORS.text }}
              dir="auto"
            />
          </div>
        </div>

        {/* Tagged section — horizontal-wrap removable pills */}
        {selected.length > 0 && (
          <div className="px-4 pb-2">
            <p
              className="text-xs font-bold mb-1.5 uppercase tracking-wide"
              style={{ color: '#65676B' }}
            >
              {t('feed.tagged')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((user) => (
                <button
                  key={user.id}
                  onClick={() => removeUser(user.id)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all active:scale-95"
                  style={{
                    backgroundColor: `${ADMIN_COLORS.primary}15`,
                    color: ADMIN_COLORS.primary,
                    border: `1px solid ${ADMIN_COLORS.primary}30`,
                  }}
                >
                  {getUserName(user)}
                  <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="h-px mx-4" style={{ backgroundColor: MODAL_COLORS.border }} />

        {/* Suggestions list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          <p
            className="text-xs font-bold mb-2 uppercase tracking-wide"
            style={{ color: '#65676B' }}
          >
            {t('feed.suggestions')}
          </p>
          <div className="space-y-0.5">
            {suggestions.map((user) => {
              const isSelected = selectedIds.has(user.id);
              return (
                <button
                  key={user.id}
                  onClick={() => toggleUser(user)}
                  className="flex items-center gap-3 w-full px-2 py-2.5 rounded-xl transition-all active:scale-[0.98]"
                  style={{
                    backgroundColor: isSelected ? `${ADMIN_COLORS.primary}10` : 'transparent',
                  }}
                >
                  {/* Avatar */}
                  <UserAvatar
                    src={user.avatarUrl}
                    name={getUserName(user)}
                    size="md"
                    className="border border-slate-300/30"
                  />
                  {/* Name + role */}
                  <div className="min-w-0 text-start flex-1">
                    <p
                      className="text-sm font-semibold truncate"
                      style={{ color: MODAL_COLORS.text }}
                    >
                      {getUserName(user)}
                    </p>
                    {user.role && (
                      <p className="text-xs truncate" style={{ color: '#65676B' }}>
                        {user.role}
                      </p>
                    )}
                  </div>
                  {/* Checkmark */}
                  {isSelected && (
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: ADMIN_COLORS.primary }}
                    >
                      <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
